/**
 * Die Anlagenart in Worten und als Symbol.
 *
 * Beides gehört zusammen und steht deshalb an einer Stelle: Der Umschalter zeigt
 * das Symbol, die Einstellungen das Wort, und die Anlagenliste beides. Lägen die
 * Zuordnungen verstreut, bekäme dieselbe Art an zwei Orten zwei Gesichter.
 *
 * **Die Werte stammen aus `SITE_KINDS` in `@hausmeter/domain`** — hier steht die
 * Übersetzung, nicht eine zweite Liste. Wächst das Enum, schlägt der Typ hier
 * fehl und nicht erst die Anzeige.
 */
import { icons, type Icon } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";

export type SiteKind = ApiComponents["schemas"]["Site"]["kind"];

export const SITE_KIND_LABELS: Record<SiteKind, string> = {
  single_family_house: "Einfamilienhaus",
  multi_family_house: "Mehrfamilienhaus",
  apartment: "Wohnung",
  commercial: "Gewerbe",
  plant: "Erzeugungsstandort",
  other: "Sonstiges",
};

/**
 * Die Arten in der Reihenfolge, in der sie zur Auswahl stehen — vom häufigsten
 * Fall zum seltensten, „Sonstiges" zuletzt. Abgeleitet aus den Beschriftungen,
 * damit nicht zwei Listen gepflegt werden müssen: Der Typ oben erzwingt die
 * Vollständigkeit, und `Object.keys` gibt die Schreibreihenfolge zurück.
 */
export const SITE_KINDS = Object.keys(SITE_KIND_LABELS) as readonly SiteKind[];

/**
 * Das Symbol je Art.
 *
 * **Es ist Wegweisung, keine Datenaussage** (ADR 0013): Es hilft beim
 * Wiedererkennen der eigenen Anlage in einer Liste und sagt nichts über
 * Verbrauch oder Erzeugung. Deshalb trägt es auch keine Größenfarbe.
 *
 * Jede Art hat ein **eigenes** Symbol. Zwei Arten dasselbe zu geben, wäre
 * schlimmer als eines für alle: Man erkennt dann einen Unterschied, wo keiner
 * angezeigt wird. Aus demselben Grund ist `other` eine Ortsnadel und kein
 * weiteres Gebäude — was unbekannt ist, wird nicht geraten (Regel 9).
 *
 * **`plant` ist ein Erzeugungsstandort** (OQ-020): Freiflächen-PV, BHKW oder
 * Windrad an einem eigenen Anschluss, kein Betriebsgelände — das ist `commercial`.
 * Das Werk-Symbol steht für „Kraftwerk" und legt sich nicht auf Photovoltaik
 * fest; ein Sonnensymbol behauptete eine Bauart, die niemand zugesagt hat, und
 * der Blitz ist die Sparte Strom.
 */
const SITE_KIND_ICONS: Record<SiteKind, Icon> = {
  single_family_house: icons.IconHome,
  multi_family_house: icons.IconBuilding,
  apartment: icons.IconDoor,
  commercial: icons.IconStore,
  plant: icons.IconFactory,
  other: icons.IconPin,
};

export function siteKindIcon(kind: SiteKind): Icon {
  return SITE_KIND_ICONS[kind];
}
